/**
 * @file ShowcaseCard 控件预览卡（含 Coding Prompt）
 * @description 展示区块标题 + 眼睛入口；弹窗查看/复制该控件的 Prompt 文档。
 *
 * @whenToUse `/showcase` 中每个控件预览区块。
 * @whenNotToUse 业务页面内容分区（用 Card / 普通 section）。
 */

import { useState, type ReactNode } from 'react'
import { Check, Copy, Eye } from 'lucide-react'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog.tsx'

export type ShowcaseCardProps = {
  /** 锚点 id */
  id: string
  /** 控件名称 */
  title: string
  /** 补充说明 */
  description?: string
  /** 标题旁图标 */
  icon?: ReactNode
  /**
   * Coding Prompt 全文（实现文档，供复制给 Agent）
   * 不传则不显示眼睛入口
   */
  prompt?: string
  children: ReactNode
}

/**
 * Showcase 预览卡：标题行右侧眼睛 → Prompt 弹窗
 */
export function ShowcaseCard({
  id,
  title,
  description,
  icon,
  prompt,
  children,
}: ShowcaseCardProps) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)

  const copyPrompt = async () => {
    if (!prompt) return
    await navigator.clipboard.writeText(prompt)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  return (
    <section id={id} className="scroll-mt-8">
      <div className="mb-4 flex flex-col gap-1">
        <div className="flex items-center gap-2">
          {icon ? <span className="text-primary">{icon}</span> : null}
          <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
          {prompt ? (
            <button
              type="button"
              aria-label={`查看 ${title} 的 Coding Prompt`}
              className="inline-flex size-7 items-center justify-center rounded-md border border-border text-muted transition-opacity duration-200 hover:opacity-90 focus-visible:opacity-90"
              onClick={() => setOpen(true)}
            >
              <Eye className="size-3.5" aria-hidden />
            </button>
          ) : null}
        </div>
        {description ? (
          <p className="text-sm text-muted">{description}</p>
        ) : null}
      </div>

      <div className="rounded-2xl border border-border bg-surface p-5">
        {children}
      </div>

      {prompt ? (
        <Dialog
          open={open}
          onOpenChange={setOpen}
          title={`${title} · Coding Prompt`}
          className="max-w-2xl"
          footer={
            <Button
              variant="primary"
              icon={
                copied ? (
                  <Check className="size-4" aria-hidden />
                ) : (
                  <Copy className="size-4" aria-hidden />
                )
              }
              onClick={copyPrompt}
            >
              {copied ? '已复制' : '复制给 Agent'}
            </Button>
          }
        >
          <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-foreground">
            {prompt}
          </pre>
        </Dialog>
      ) : null}
    </section>
  )
}
